import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { useAppContext } from '@contexts/app';
import { useSchedulerContext } from '@contexts/scheduler';
import { Button } from '@components/ui/button';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { shortDate } from '@utils/i18n';
import { parseISO } from 'date-fns';
import { RelatedMultischedule } from '@services/schedules/internal/types';
import { toZonedTime } from 'date-fns-tz';
import { Avatar } from '@components/avatar';
import { cn } from '@utils/cn';
import { parseSchedulesForDates } from '@forms/schedules/internal/helpers';
import { getForemanName } from '@components/schedules/dialogs/internal/inputs/extendDays/helpers';
import {
  MAX_DAYS_WITHOUT_TABS,
  getCurrentWeek,
  getDayChangeStatus,
  getScheduleDisplayDate,
} from './helpers';

export const SchedulerSidebarDays = () => {
  const { relatedMultischedules, crewChangeScope, currentSchedule, extensionDates } =
    useSchedulerSidebar();
  const { tenant } = useAppContext();
  const { active } = useSchedulerContext();
  const { t } = useTranslation('app', { keyPrefix: 'schedule.days' });
  const [week, setWeek] = useState(() => getCurrentWeek(relatedMultischedules || {}));

  if (!relatedMultischedules) return null;

  const days = Object.values(relatedMultischedules).flat();
  if (!days.length) return null;

  const withTabs = days.length > MAX_DAYS_WITHOUT_TABS;
  const visibleDays = withTabs ? relatedMultischedules[week] || [] : days;
  const weeks = Object.keys(relatedMultischedules).sort((a, b) => Number(a) - Number(b));
  const weekIndex = weeks.indexOf(week);
  const weekRange = [visibleDays[0], visibleDays.at(-1)]
    .filter((el): el is RelatedMultischedule => Boolean(el))
    .map((el) => shortDate(toZonedTime(parseISO(el.startsAt), tenant.zone)));
  const newDays =
    (!withTabs || weekIndex === weeks.length - 1) &&
    active?.schedule?.startsAt &&
    active.schedule.endsAt
      ? parseSchedulesForDates(
          active.schedule.startsAt,
          active.schedule.endsAt,
          extensionDates || [],
          tenant.zone,
        )
      : [];

  return (
    <div className="flex flex-col gap-2">
      {withTabs && (
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            className="h-7 w-7 p-0"
            disabled={weekIndex <= 0}
            aria-label={t('previousWeek')}
            onClick={() => setWeek(weeks[weekIndex - 1])}>
            <SvgConsumer id="arrow" className="-scale-x-100" />
          </Button>
          <span className="text-xs font-semibold text-neutral-700">
            {[...new Set(weekRange)].join(' – ')}
            <span className="ml-1.5 font-normal text-neutral-400">
              {t('weekOf', { week: weekIndex + 1, total: weeks.length })}
            </span>
          </span>
          <Button
            variant="ghost"
            className="h-7 w-7 p-0"
            disabled={weekIndex >= weeks.length - 1}
            aria-label={t('nextWeek')}
            onClick={() => setWeek(weeks[weekIndex + 1])}>
            <SvgConsumer id="arrow" />
          </Button>
        </div>
      )}
      <ul className="flex flex-col gap-1">
        {visibleDays.map((el) => {
          const status = getDayChangeStatus({
            schedule: el,
            currentSchedule,
            crewChangeScope,
            zone: tenant.zone,
          });

          return (
            <li
              key={el.id}
              className={cn(
                'grid grid-cols-[24px_1fr] items-center gap-x-2 rounded border-l-2 px-2 py-1.5',
                status.affected
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-transparent bg-transparent',
              )}>
              <Avatar
                className={cn('h-6 w-6 text-xs', !status.affected && 'opacity-50')}
                name={el.foreman?.name || ''}
              />
              <span
                className={cn(
                  'text-xs',
                  status.affected ? 'font-medium text-neutral-800' : 'text-neutral-400',
                )}>
                {status.current && active?.schedule?.startsAt && active.schedule.endsAt
                  ? getScheduleDisplayDate(
                      active.schedule.startsAt,
                      active.schedule.endsAt,
                      tenant.zone,
                    )
                  : getScheduleDisplayDate(el.startsAt, el.endsAt, tenant.zone)}
              </span>
              <div className="col-start-2 flex flex-wrap items-center gap-1.5">
                {status.current && (
                  <span className="text-[10px] font-semibold text-primary-600">{t('thisDay')}</span>
                )}
                {status.reason && (
                  <span className="text-[10px] text-neutral-500">
                    {t('notAffected', { reason: t(`reasons.${status.reason}`) })}
                  </span>
                )}
                {!el.defaultCrew && (
                  <span className="rounded-md bg-neutral-50 px-2 py-0.5 text-[10px] font-medium text-neutral-500">
                    {t('customCrew')}
                  </span>
                )}
              </div>
            </li>
          );
        })}
        {newDays.map((el) => (
          <li
            key={el.startsAt}
            className="grid grid-cols-[24px_1fr] items-center gap-x-2 rounded border-l-2 border-dashed border-primary-500 bg-primary-50 px-2 py-1.5">
            <Avatar className="h-6 w-6 text-xs" name={getForemanName(active.schedule.foreman)} />
            <span className="text-xs font-medium text-neutral-800">
              {getScheduleDisplayDate(el.startsAt, el.endsAt, tenant.zone)}
            </span>
            <div className="col-start-2">
              <span className="text-[10px] font-semibold text-primary-600">{t('newDay')}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
